import { Download } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { STAFF_COLUMN_LABELS, type StaffMemberView, type StaffUploadPreview } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { DropZone } from '../../../components/DropZone';
import { SaveBar } from '../../../components/SaveBar';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import sheet from '../../../components/SheetTable.module.css';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useApplyStaffUpload, usePreviewStaffUpload, useStaffList } from '../api/staff-api';
import styles from './StaffListPage.module.css';

/**
 * Admin → Staff list (ADR 0074, workflow review 2026-10-29). HR uploads the staff sheet from
 * the HR system; "Who is being replaced?" on a hire request then lists only the requester's
 * own team. The upload is read and shown first — counts, problems first — and saved only on
 * "Replace the staff list". People missing from the new sheet are taken off, never deleted.
 */
export function StaffListPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const list = useStaffList();
  const previewUpload = usePreviewStaffUpload();
  const apply = useApplyStaffUpload();
  const show = useToasts((state) => state.show);
  const [preview, setPreview] = useState<StaffUploadPreview | null>(null);

  if (!canView) return <ForbiddenState resource="the staff list" contact="Your admin" />;
  if (list.isPending) return <SkeletonLines count={10} />;
  if (list.error) return <ServerErrorState onRetry={() => void list.refetch()} />;

  const last = list.data.lastUploadedAt
    ? `Last uploaded ${new Date(list.data.lastUploadedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}${list.data.lastUploadedByName ? ` by ${list.data.lastUploadedByName}` : ''} · ${formatNumber(list.data.total)} people`
    : 'Not uploaded yet';

  function read(file: File): void {
    previewUpload.mutate(file, {
      onSuccess: (result) => setPreview(result),
      onError: (cause) => show({ tone: 'danger', message: "The sheet couldn't be read", detail: errorMessage(cause) }),
    });
  }

  function replace(): void {
    if (!preview) return;
    apply.mutate(preview.uploadId, {
      onSuccess: (saved) => {
        setPreview(null);
        show({ tone: 'success', message: `Staff list replaced · ${formatNumber(saved.total)} people` });
      },
      onError: (cause) => show({ tone: 'danger', message: 'The staff list was not replaced', detail: errorMessage(cause) }),
    });
  }

  const isRefused = preview !== null && preview.missingColumns.length > 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Staff list</h1>
          <p className={styles.lead}>Used for "Who is being replaced?". Each team lead sees only the people who report to them.</p>
        </div>
        <Button variant="outline" icon={Download} onClick={downloadBlankSheet}>
          Download a blank sheet
        </Button>
      </header>

      <Card>
        <div className={styles.body}>
          {canEdit && (
            <DropZone
              label="Staff sheet"
              title="Drop the staff sheet here, or choose a file"
              hint="CSV or Excel · columns: Name, Email, Job title, Team, Manager email"
              accept=".csv,.xlsx"
              isPending={previewUpload.isPending}
              onFile={read}
            />
          )}

          {preview && (
            <>
              <div className={styles.stats}>
                <Stat value={preview.peopleRead} label="people read" />
                <Stat value={preview.managersFound} label="managers found" />
                <Stat value={preview.managerMissing} label="manager not found" isWarning={preview.managerMissing > 0} />
                {preview.leaving > 0 && <Stat value={preview.leaving} label="taken off the list" isWarning />}
              </div>
              {isRefused && (
                <p className={styles.problem} role="alert">
                  The sheet has no {preview.missingColumns.map((column) => `"${STAFF_COLUMN_LABELS[column]}"`).join(' or ')} column. Add it and choose the file again.
                </p>
              )}
              {preview.skipped.length > 0 && (
                <p className={styles.note}>
                  {preview.skipped.length} {preview.skipped.length === 1 ? 'row was' : 'rows were'} skipped:{' '}
                  {preview.skipped.slice(0, 5).map((skip) => `row ${skip.row} (${skip.reason.toLowerCase()})`).join(', ')}
                  {preview.skipped.length > 5 ? '…' : ''}
                </p>
              )}
            </>
          )}

          <StaffTable members={preview ? preview.preview : list.data.members} />

          {preview ? (
            <SaveBar status={last}>
              <Button variant="ghost" onClick={() => setPreview(null)}>
                Cancel
              </Button>
              <Button variant="solid" isPending={apply.isPending} disabled={isRefused || preview.peopleRead === 0} onClick={replace}>
                Replace the staff list
              </Button>
            </SaveBar>
          ) : (
            <p className={styles.note}>{last}</p>
          )}
        </div>
      </Card>
    </div>
  );
}

function Stat({ value, label, isWarning = false }: { readonly value: number; readonly label: string; readonly isWarning?: boolean }): ReactNode {
  return (
    <div className={styles.stat}>
      <span className={isWarning ? styles.statWarn : styles.statValue}>{formatNumber(value)}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

function StaffTable({ members }: { readonly members: readonly StaffMemberView[] }): ReactNode {
  if (members.length === 0) return <p className={styles.note}>Nobody is on the staff list yet.</p>;
  return (
    <div className={styles.tableWrap}>
      <table className={sheet.sheet}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Job title</th>
            <th>Team</th>
            <th>Reports to</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.id} className={member.isManagerMissing ? styles.warnRow : undefined}>
              <td>{member.fullName}</td>
              <td>{member.jobTitle ?? '—'}</td>
              <td>{member.team ?? '—'}</td>
              <td className={member.isManagerMissing ? styles.warnText : undefined}>
                {member.isManagerMissing ? `"${member.reportsTo ?? ''}" isn't in the sheet` : (member.reportsTo ?? '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A CSV with just the header row, made in the browser. */
function downloadBlankSheet(): void {
  const header = 'Name,Email,Job title,Team,Manager email\n';
  const url = URL.createObjectURL(new Blob([header], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'staff-list.csv';
  link.click();
  URL.revokeObjectURL(url);
}
